import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/d1';
import { useAuth } from './useAuth';
import type { Demand, DemandAssistResponse, DemandPlatformStats, OwnerDemandPrefs, OwnerDemandPrefsInput } from '../types';

export const demandKeys = {
  all: ['demands'] as const,
  list: (userId?: string | null) => [...demandKeys.all, 'list', userId || 'anonymous'] as const,
  stats: () => [...demandKeys.all, 'stats'] as const,
  my: (userId?: string | null) => [...demandKeys.all, 'my', userId || 'anonymous'] as const,
  responses: (id: string, userId?: string | null) => [...demandKeys.all, 'responses', id, userId || 'anonymous'] as const,
  prefs: (userId?: string | null) => ['ownerDemandPrefs', userId || 'anonymous'] as const,
};

export function useDemands() {
  const qc = useQueryClient();
  const { isSignedIn, user } = useAuth();
  const userId = user?.id;

  const listQuery = useQuery<Demand[]>({
    queryKey: demandKeys.list(userId),
    queryFn: () => api.demands.list(),
    staleTime: 60_000,
    enabled: true,
  });

  const myQuery = useQuery<Demand[]>({
    queryKey: demandKeys.my(userId),
    queryFn: () => api.demands.my(),
    staleTime: 30_000,
    enabled: isSignedIn === true && !!userId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<Demand> & { expires_in_days: number }) => api.demands.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: demandKeys.my(userId) });
      qc.invalidateQueries({ queryKey: demandKeys.all });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.demands.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: demandKeys.my(userId) });
      qc.invalidateQueries({ queryKey: demandKeys.all });
    },
  });

  const respondMutation = useMutation({
    mutationFn: ({ demandId, listingId, message }: { demandId: string; listingId: string; message: string }) =>
      api.demands.respond(demandId, { listing_id: listingId, message }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: demandKeys.all });
    },
  });

  // AI-assist for the demand form (see components/demands/DemandForm.tsx).
  // Never persists anything — only returns a suggested description to review.
  const assistMutation = useMutation<DemandAssistResponse, Error, Record<string, any>>({
    mutationFn: (data) => api.demands.assist(data),
  });

  return { listQuery, myQuery, createMutation, removeMutation, respondMutation, assistMutation };
}

export function useDemandResponses(demandId: string | undefined) {
  const { user } = useAuth();
  const userId = user?.id;
  return useQuery({
    queryKey: demandKeys.responses(demandId!, userId),
    queryFn: async () => {
      const res = await api.demands.getResponses(demandId!);
      return res; // api.demands.getResponses already returns the array
    },
    enabled: !!demandId,
    staleTime: 30_000,
  });
}

export function useDemandStats() {
  return useQuery<DemandPlatformStats>({
    queryKey: demandKeys.stats(),
    queryFn: () => api.demands.stats(),
    staleTime: 60_000,
  });
}

export function useOwnerDemandPrefs() {
  const qc = useQueryClient();
  const { isSignedIn, user } = useAuth();
  const userId = user?.id;

  // Owner-only endpoint — guests would just collect 401s in the console
  const prefsQuery = useQuery<OwnerDemandPrefs | null>({
    queryKey: demandKeys.prefs(userId),
    queryFn: () => api.ownerPrefs.get(),
    staleTime: 5 * 60_000,
    enabled: !!isSignedIn && !!userId,
  });

  const saveMutation = useMutation({
    mutationFn: (data: OwnerDemandPrefsInput) => api.ownerPrefs.save(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: demandKeys.prefs(userId) });
    },
  });

  return { prefsQuery, saveMutation };
}
